// Client-side helpers for hitting AdSpeed's serving endpoints directly from
// the browser. This is intentionally NOT going through our sponsor worker —
// AdSpeed gates ad selection by request context (User-Agent, Referer, IP).
// Calls from our worker get "AS#404 NO AD FOUND IN ROTATION" because AdSpeed
// treats Cloudflare worker IPs as non-publisher requests. Calls from the
// browser look like real publisher embeds and AdSpeed serves real ads.
//
// The downside is we depend on AdSpeed returning permissive CORS headers
// (Access-Control-Allow-Origin) for fetch() to read the response. If they
// don't, this will fail with a network error — we'd need to fall back to
// rendering through an <iframe> or use a hidden <video> with the server-side
// VAST URL embedded.

// TODO: drive these from a /sponsors/config endpoint if the publisher OID or
// serving domain ever varies per environment.
const SERVING_BASE = 'https://ads.tenband.tv/ad.php'
const PUB_OID = '28740'

export const ADSPEED_DASHBOARD_AD_URL = (id) =>
  `https://advertiser.tenband.tv/Ads/show.html?id=${id}`

export function servingImgUrl({ aid, zid, width, height }) {
  const idParam = aid ? `aid=${aid}` : `zid=${zid}`
  return `${SERVING_BASE}?do=img&${idParam}&oid=${PUB_OID}&wd=${width}&ht=${height}&_cb=${Date.now()}`
}

export function servingClickUrl({ aid, zid, width, height }) {
  const idParam = aid ? `aid=${aid}` : `zid=${zid}`
  return `${SERVING_BASE}?do=clk&${idParam}&oid=${PUB_OID}&wd=${width}&ht=${height}`
}

export function servingHtmlUrl({ aid, zid, width, height }) {
  const idParam = aid ? `aid=${aid}` : `zid=${zid}`
  return `${SERVING_BASE}?do=html&${idParam}&oid=${PUB_OID}&wd=${width}&ht=${height}&target=_blank&_cb=${Date.now()}`
}

function vastUrl({ aid, zid }) {
  const idParam = aid ? `aid=${aid}` : `zid=${zid}`
  // wd/ht=-1 means "any dimensions" — without these, AdSpeed's selection logic
  // rejects with AS#404 NO AD FOUND. vastver=3 requests VAST 3. cb is the
  // cache buster AdSpeed's own dashboard includes.
  return `${SERVING_BASE}?do=vast&${idParam}&oid=${PUB_OID}&wd=-1&ht=-1&vastver=3&cb=${Date.now()}`
}

// Fetch VAST XML directly from AdSpeed in the browser, parse with DOMParser.
// Returns { ok, mediafile, impression, clickThrough, duration, servedAdId, raw, error }.
export async function fetchAndParseVast({ aid, zid }) {
  const url = vastUrl({ aid, zid })
  let xml
  try {
    const res = await fetch(url)
    xml = await res.text()
  } catch (e) {
    return { ok: false, error: e.message, raw: '', _hint: 'CORS or network — AdSpeed may not allow cross-origin fetch.' }
  }

  let doc
  try {
    doc = new DOMParser().parseFromString(xml, 'text/xml')
  } catch (e) {
    return { ok: false, error: 'XML parse failed: ' + e.message, raw: xml }
  }

  // Detect AdSpeed error responses (sometimes returned as an <a> tag instead of VAST).
  if (xml.includes('NO AD FOUND IN ROTATION') || xml.includes('AS#404')) {
    return { ok: false, error: 'AdSpeed: no ad found in rotation', raw: xml }
  }
  // Generic AdSpeed error envelope
  const errEl = doc.querySelector('Error')
  if (errEl && !doc.querySelector('MediaFile')) {
    return { ok: false, error: errEl.textContent.trim() || 'AdSpeed error', raw: xml }
  }

  const mediafile = doc.querySelector('MediaFile')?.textContent.trim() || null
  const impression = doc.querySelector('Impression')?.textContent.trim() || null
  const clickThrough = doc.querySelector('ClickThrough')?.textContent.trim() || null
  const duration = doc.querySelector('Duration')?.textContent.trim() || null
  const servedAdId = doc.querySelector('VAST > Ad')?.getAttribute('id') || null

  if (!mediafile) {
    return { ok: false, error: 'No <MediaFile> in VAST response', raw: xml }
  }
  return { ok: true, mediafile, impression, clickThrough, duration, servedAdId, raw: xml }
}

// Fire a beacon URL by loading it as an Image — bypasses CORS for tracking pixels.
export function fireBeacon(url) {
  if (!url) return
  const img = new Image()
  img.src = url
}
